import type { PreviewReview, ReviewSourceId } from '@flexbyt/shared';
import { site } from './site.js';

/**
 * Asking the API to read somebody's listing, from the marketing site.
 *
 * ===========================================================================
 * What this makes possible
 * ===========================================================================
 * A stranger arrives from a search for "put Google reviews on my website",
 * pastes their own listing, and sees their own reviews in a real widget before
 * they have an account. That is the most persuasive thing this site can do, and
 * until now it could not do it: the source pages took the link, checked its
 * hostname in the browser, and then showed *sample* reviews with a line
 * explaining that nothing is fetched until you sign up.
 *
 * That line was honest, and it was a description of a missing endpoint.
 *
 * ===========================================================================
 * Why it is safe now, in one paragraph
 * ===========================================================================
 * Because the endpoint spends money — an Apify actor run per listing — and
 * therefore has to be defended from four different things at once: automation
 * (Cloudflare Turnstile), being used as a general-purpose scraping API (a host
 * allow-list, a six-review ceiling, and a response stripped of anything worth
 * building on), one person hammering it (per-address quotas) and many people
 * hammering it (one global daily budget). Plus a day-long cache, so the common
 * repeats — three layouts, a link shared with a colleague — cost nothing.
 *
 * None of that lives here. This module is the client; the rules are in the API,
 * where they cannot be edited by the person they apply to. See
 * `apps/api/src/modules/imports/public-routes.ts` for the whole argument.
 *
 * ===========================================================================
 * Every failure is a shrug
 * ===========================================================================
 * The site works completely without this endpoint — it did for six phases. So
 * a deployment with the feature off, an API that is down, a listing with no
 * reviews on it and a visitor over their quota all reach the same place: the
 * sample reviews that were there before, and a sentence saying why. Nothing on
 * these pages is allowed to depend on the fetch succeeding.
 */

export type ImportPreviewConfig = {
  enabled: boolean;
  /** Null when the feature is off. Public by design — it is printed into the page. */
  turnstileSiteKey: string | null;
  /** How many reviews a preview returns, so the copy can say the true number. */
  limit: number;
  sources: { id: ReviewSourceId; label: string; hosts: string[] }[];
};

const DISABLED: ImportPreviewConfig = {
  enabled: false,
  turnstileSiteKey: null,
  limit: 0,
  sources: [],
};

function endpoint(path: string): string {
  return new URL(`/api/public/imports${path}`, site.api).toString();
}

/**
 * Whether this deployment can read a listing, and with which key.
 *
 * Read from the API rather than from a build-time flag for the same reason the
 * dashboard reads its Turnstile key from `/api/auth/providers`: a variable
 * baked into this bundle lets the site offer a button against a server that
 * will refuse every call, and the person who finds out is a visitor.
 *
 * Called from a route loader, so the answer is known before the page paints and
 * the paste field never flickers between two versions of itself.
 */
export async function fetchImportPreviewConfig(
  { signal }: { signal?: AbortSignal } = {}
): Promise<ImportPreviewConfig> {
  try {
    const response = await fetch(endpoint('/config'), {
      signal,
      headers: { accept: 'application/json' },
    });
    if (!response.ok) return DISABLED;
    return (await response.json()) as ImportPreviewConfig;
  } catch {
    /*
      Swallowed deliberately, including the abort. Whatever went wrong, the
      answer for this page is the same and it is not an exception thrown out of
      a route loader: the source page falls back to sample reviews, which is
      what it showed before this endpoint existed.
    */
    return DISABLED;
  }
}

export type ImportPreviewResult =
  | { ok: true; reviews: PreviewReview[]; pageUrl: string }
  | { ok: false; message: string };

type PreviewResponseReview = Omit<PreviewReview, 'source'> & {
  source: ReviewSourceId;
};

/**
 * Read one listing.
 *
 * Returns a refusal rather than throwing, because every refusal this endpoint
 * issues is something a person needs to read — "that is a Google search, not
 * your listing", "we could not find any reviews there", "you have previewed a
 * few already". Those are the useful half of the feature, and an exception
 * would flatten all of them into "something went wrong".
 */
export async function requestImportPreview({
  source,
  pageUrl,
  turnstileToken,
  signal,
}: {
  source: ReviewSourceId;
  pageUrl: string;
  turnstileToken: string;
  signal?: AbortSignal;
}): Promise<ImportPreviewResult> {
  let response: Response;

  try {
    response = await fetch(endpoint('/preview'), {
      method: 'POST',
      signal,
      headers: { 'content-type': 'application/json', accept: 'application/json' },
      body: JSON.stringify({ source, pageUrl, turnstileToken }),
    });
  } catch {
    return {
      ok: false,
      message:
        'We could not reach ReviewsJet just then. Try again, or carry on with the sample reviews below.',
    };
  }

  let body: { reviews?: PreviewResponseReview[]; pageUrl?: string; message?: string };
  try {
    body = (await response.json()) as typeof body;
  } catch {
    body = {};
  }

  if (!response.ok) {
    return {
      ok: false,
      message:
        body.message ??
        'We could not read that listing. Check the address, or carry on with the sample reviews below.',
    };
  }

  const reviews = body.reviews ?? [];
  if (reviews.length === 0) {
    return {
      ok: false,
      message:
        'We did not find any reviews on that page. Check the address, or carry on with the sample reviews below.',
    };
  }

  return {
    ok: true,
    pageUrl: body.pageUrl ?? pageUrl,
    /*
      Normalised into the renderer's own shape rather than trusted as-is. The
      API sends exactly this today; spelling it out means a field added there
      cannot arrive on a review card by accident, and `isSample: false` is
      asserted here rather than read — these are somebody's real reviews and
      must never be marked as the demo set.
    */
    reviews: reviews.map((review) => ({
      id: review.id,
      authorName: review.authorName,
      authorAvatar: review.authorAvatar ?? null,
      rating: review.rating,
      title: review.title ?? '',
      content: review.content ?? '',
      reviewDate: review.reviewDate ?? '',
      source: review.source,
      pictures: review.pictures ?? [],
      isSample: false,
    })),
  };
}
